import { IUserCreator, UserModel, UserStatus } from "@truckdown/accounts";
import { defineComponent, ref, Ref, watch } from 'vue';
import context from '@truckdown/systems';
import { isCsrOrAdmin, isUserInRole } from "../../Utilities/Utilities"

interface IData {
    isLoading: Ref<boolean>,
    sortNameAsc: Ref<boolean>,
    sortNameDesc: Ref<boolean>,
    sortEmailAsc: Ref<boolean>,
    sortEmailDesc: Ref<boolean>,
    sortMobileAsc: Ref<boolean>,
    sortMobileDesc: Ref<boolean>,
    sortStatusAsc: Ref<boolean>,
    sortStatusDesc: Ref<boolean>,
    sortLastModifiedAsc: Ref<boolean>,
    sortLastModifiedDesc: Ref<boolean>,
    sortRightsAsc: Ref<boolean>,
    sortRightsDesc: Ref<boolean>,
    sortedArray: Ref<UserModel[]>,
    isAdmin: Ref<boolean>,
    showRemoveInviteModal: Ref<boolean>,
    removeInviteUserName: Ref<string>,
    removeInviteUserEmail: Ref<string>,
    removeInviteAccountId: Ref<string>,
    removeInviteError: Ref<string | null>
}

const getInitialData = function (props: any) {
    return {
        isLoading: ref<boolean>(false),
        sortNameAsc: ref<boolean>(false),
        sortNameDesc: ref<boolean>(false),
        sortEmailAsc: ref<boolean>(false),
        sortEmailDesc: ref<boolean>(false),
        sortMobileAsc: ref<boolean>(false),
        sortMobileDesc: ref<boolean>(false),
        sortStatusAsc: ref<boolean>(false),
        sortStatusDesc: ref<boolean>(false),
        sortLastModifiedAsc: ref<boolean>(false),
        sortLastModifiedDesc: ref<boolean>(false),
        sortRightsAsc: ref<boolean>(false),
        sortRightsDesc: ref<boolean>(false),
        sortedArray: ref<UserModel[]>(props.users),
        isAdmin: ref<boolean>(props.isAdmin),
        showRemoveInviteModal: ref<boolean>(false),
        removeInviteUserName: ref<string>(''),
        removeInviteUserEmail: ref<string>(''),
        removeInviteAccountId: ref<string>(''),
        removeInviteError: ref<string | null>(null)
    }
}

const listEvents = {
    sort: 'sort',
    reload: 'reload'
}

const resetSortBy = function (data: IData) {
    data.sortNameAsc.value = false;
    data.sortNameDesc.value = false;
    data.sortEmailAsc.value = false;
    data.sortEmailDesc.value = false;
    data.sortMobileAsc.value = false;
    data.sortMobileDesc.value = false;
    data.sortStatusAsc.value = false;
    data.sortStatusDesc.value = false;
    data.sortLastModifiedAsc.value = false;
    data.sortLastModifiedDesc.value = false;
    data.sortRightsAsc.value = false;
    data.sortRightsDesc.value = false;
}

const getMethods = function (data: IData, ctx: any) {
    return {
        sortBy: function (field: string) {
            field = field.toLowerCase();
            resetSortBy(data);

            if (field == "name_asc") {
                data.sortedArray.value.sort((a, b) => a.firstName.localeCompare(b.firstName));
                data.sortNameAsc.value = true;
            } else if (field == "name_desc") {
                data.sortedArray.value.sort((a, b) => b.firstName.localeCompare(a.firstName));
                data.sortNameDesc.value = true;
            } else if (field == "email_asc") {
                data.sortedArray.value.sort((a, b) => a.email.localeCompare(b.email));
                data.sortEmailAsc.value = true;
            } else if (field == "email_desc") {
                data.sortedArray.value.sort((a, b) => b.email.localeCompare(a.email));
                data.sortEmailDesc.value = true;
            } else if (field == "mobile_asc") {
                data.sortedArray.value.sort((a, b) => (a.mobile ?? "").localeCompare(b.mobile ?? ""));
                data.sortMobileAsc.value = true;
            } else if (field == "mobile_desc") {
                data.sortMobileDesc.value = true;
                data.sortedArray.value.sort((a, b) => (b.mobile ?? "").localeCompare(a.mobile ?? ""));
            } else if (field == "status_asc") {
                data.sortedArray.value.sort((a, b) => a.status - b.status);
                data.sortStatusAsc.value = true;
            } else if (field == "status_desc") {
                data.sortedArray.value.sort((a, b) => b.status - a.status);
                data.sortStatusDesc.value = true;
            } else if (field == "lastmodified_asc") {
                data.sortedArray.value.sort((a, b) => a.modified.toString().localeCompare(b.modified.toString()));
                data.sortLastModifiedAsc.value = true;
            } else if (field == "lastmodified_desc") {
                data.sortedArray.value.sort((a, b) => b.modified.toString().localeCompare(a.modified.toString()));
                data.sortLastModifiedDesc.value = true;
            } else if (field == "rights_asc") {
                data.sortedArray.value.sort((a, b) => this.getRole(a).localeCompare(this.getRole(b)));
                data.sortRightsAsc.value = true;
            } else if (field == "rights_desc") {
                data.sortedArray.value.sort((a, b) => this.getRole(b).localeCompare(this.getRole(a)));
                data.sortRightsDesc.value = true;
            } else {
                data.sortedArray.value.sort((a, b) => a.firstName.localeCompare(b.firstName));
                data.sortNameAsc.value = true;
            }
        },
        getRole: function (user: UserModel) {
            var rights = [];

            if (isUserInRole(user, "System Admin") == true) {
                rights.push("System Admin");
            } else if (isUserInRole(user, "TruckDown CSR") == true) {
                rights.push("TruckDown CSR");
            } else if (isUserInRole(user, "General Admin") == true) {
                rights.push("Admin");
            } 

            var vsradmin = user.claims.filter(c => c.type == "VSRADMIN").map(claim => claim.value)[0];
            if (vsradmin == "True") {
                rights.push("Vendor Service Request Admin");
            }

            var vsrreadonly = user.claims.filter(c => c.type == "VSRREADONLY").map(claim => claim.value)[0];
            if (vsrreadonly == "True") {
                rights.push("Vendor Service Request Read-only");
            }

            var vsr = user.claims.filter(c => c.type == "VSR").map(claim => claim.value)[0];
            if (vsr == "True") {
                rights.push("Vendor Service Requests Select Locations");
            }

            var vsrtech = user.claims.filter(c => c.type == "VSRTECH").map(claim => claim.value)[0];
            if (vsrtech == "True") {
                rights.push("Vendor Service Request Technician");
            }

            var fsradmin = user.claims.filter(c => c.type == "FSRADMIN").map(claim => claim.value)[0];
            if (fsradmin == "True") {
                rights.push("Fleet Service Request Admin");
            }

            var fsrreadonly = user.claims.filter(c => c.type == "FSRREADONLY").map(claim => claim.value)[0];
            if (fsrreadonly == "True") {
                rights.push("Fleet Service Request Read-only");
            }

            var fsrdisabled = user.claims.filter(c => c.type == "FSRDISABLED").map(claim => claim.value)[0];
            if (fsrdisabled == "True") {
                rights.push("Fleet Service Requests Disabled");
            }

            return rights.join("\r\n");
        },
        getViewUserLink: function (user: UserModel) {
            if (data.isAdmin.value) {
                return `/accounts/admin/user/${encodeURIComponent(user.id)}`;
            } else {
                return `/accounts/user/${encodeURIComponent(user.id)}`;
            }
        },
        getProfilePicture: function (user: UserModel) {
            return context.profileImagePath + user.settings.profilepic;
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        },
        getStatus: function (status: UserStatus) {
            return UserStatus[status];
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC) {
                return new Date(dateUTC).toLocaleString();
            } else
                return undefined;
        },
        showRemoveInvite: function (user: UserModel) {
            data.removeInviteError.value = null;

            data.removeInviteUserName.value = "" + user.firstName + " " + user.lastName;
            data.removeInviteUserEmail.value = user.email;
            data.removeInviteAccountId.value = user.aid;

            data.showRemoveInviteModal.value = true;
        },
        closeRemoveInviteModal: function () {
            data.showRemoveInviteModal.value = false;
            data.removeInviteUserName.value = "";
            data.removeInviteUserEmail.value = "";
            data.removeInviteAccountId.value = "";
        },
        removeInviteSubmit: async function () {
            data.removeInviteError.value = null;
            let userCreator = await context.getService<IUserCreator>('IUserCreator');
            let response = await userCreator.removeInvite(data.removeInviteAccountId.value, data.removeInviteUserEmail.value);

            if (response && response.ok == true) {
                data.showRemoveInviteModal.value = false;
                data.removeInviteUserName.value = "";
                data.removeInviteUserEmail.value = "";
                data.removeInviteAccountId.value = "";

                ctx.emit('reload');
            } else {
                data.removeInviteError.value = response.message ?? "Error removing invite.";
                data.removeInviteError.value = "Error removing invite.";
            }
        }
    };
}

export default defineComponent({
    name: "UserList",
    props: {
        users: Array as () => UserModel[],
        isAdmin: Boolean
    },
    emits: [listEvents.sort, listEvents.reload],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(data, ctx);

        watch(() => props.users, (newVal, oldVal) => {
            data.sortedArray.value = newVal ?? [];
        }, { deep: true })

        return {
            ...data,
            ...methods
        }
    }
});